<template>
  <div class="py-8">
    <header class="cy-page-head">
      <p class="cy-kicker">{{ $t('shop.nav_free') }}</p>
      <h1 class="cy-page-title">{{ $t('shop.free_title', 'Free Downloads') }}</h1>
      <p class="cy-page-sub">{{ $t('shop.free_sub', 'Hand-picked tracks, releases and sample packs — yours to keep, no payment.') }}</p>
    </header>

    <!-- Quota strip -->
    <div v-if="quota" class="quota-strip">
      <template v-if="loggedIn">
        <div>
          <p class="text-white font-semibold text-sm">
            <span class="text-primary text-xl font-black">{{ quota.totalAvailable }}</span>
            free {{ quota.totalAvailable === 1 ? 'download' : 'downloads' }} available
            <span v-if="quota.bonus > 0" class="bonus-chip">+{{ quota.bonus }} referral bonus</span>
          </p>
          <p class="text-text-muted text-xs">
            <template v-if="quota.totalAvailable > 0">Pick anything below.</template>
            <template v-else>Your weekly claim unlocks {{ resetLabel }} — or invite a friend for an extra one.</template>
          </p>
        </div>
      </template>
      <template v-else>
        <div>
          <p class="text-white font-semibold text-sm">
            {{ quota.quota }} free {{ quota.quota === 1 ? 'download' : 'downloads' }} every week
          </p>
          <p class="text-text-muted text-xs">Create a free account to start claiming.</p>
        </div>
        <div class="flex gap-2">
          <NuxtLink to="/login" class="btn-ghost">Sign in</NuxtLink>
          <NuxtLink to="/signup" class="btn-primary">Sign up free</NuxtLink>
        </div>
      </template>
    </div>

    <ShopNewsletterSignup
      variant="panel"
      source="free-download"
      :title="gatedCount > 0
        ? 'Unlock the newsletter-only downloads'
        : 'Get told when new free downloads land'"
      :subtitle="gatedCount > 0
        ? `${gatedCount} ${gatedCount === 1 ? 'item is' : 'items are'} reserved for subscribers.`
        : 'One mail when the pool changes. Nothing else.'"
    />

    <div v-if="loggedIn && referral" class="panel">
      <div class="flex-1 min-w-0">
        <p class="text-white font-semibold text-sm">
          Invite a friend, get an extra download
        </p>
        <p class="text-text-muted text-xs">
          <template v-if="referral.qualified > 0">
            {{ referral.qualified }} {{ referral.qualified === 1 ? 'friend has' : 'friends have' }} joined
            — that earned you {{ referral.bonusLifetime }} bonus
            {{ referral.bonusLifetime === 1 ? 'download' : 'downloads' }}.
          </template>
          <template v-else-if="referral.pending > 0">
            {{ referral.pending }} invited, waiting for
            {{ referral.pending === 1 ? 'them' : 'them' }} to confirm their email.
          </template>
          <template v-else>
            They get the free downloads, you get {{ referral.rewardPerReferral }} extra claim
            once they confirm their email.
          </template>
        </p>
      </div>
      <div class="flex gap-2 items-center">
        <code class="ref-link">{{ referral.shareUrl }}</code>
        <button class="btn-ghost" @click="copyReferral">{{ copied ? 'Copied' : 'Copy' }}</button>
      </div>
    </div>

    <div v-if="pending" class="grid-cards">
      <div v-for="n in 8" :key="n" class="skeleton-card" />
    </div>

    <div v-else-if="!items.length" class="empty">
      <p class="text-white font-semibold">Nothing here yet</p>
      <p class="text-text-muted text-sm">Check back soon — the pool changes regularly.</p>
    </div>

    <div v-else class="grid-cards">
      <div v-for="item in items" :key="item._id" class="fcard">
        <div class="fcard-art-wrap">
          <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.title" class="fcard-art" loading="lazy" />
          <div v-else class="fcard-art fcard-art-empty">♪</div>
          <span v-if="item.featured" class="badge-featured">Pick of the week</span>
          <span v-if="item.owned" class="badge-owned">In your library</span>
          <!-- Hear it before spending a claim. Only rendered when there
               is something to play: a sample pack is an archive, not a
               stream, and a dead button is worse than none. -->
          <button
            v-if="item.preview"
            type="button"
            class="fcard-play"
            :class="{ playing: isPreviewing(item) }"
            :title="isPreviewing(item) ? $t('shop.free_pause') : $t('shop.free_play')"
            :aria-label="isPreviewing(item) ? $t('shop.free_pause') : $t('shop.free_play')"
            @click.stop.prevent="togglePreview(item)"
          >
            <NuxtIcon :name="isPreviewing(item) ? 'mdi:pause' : 'mdi:play'" size="20" />
          </button>
        </div>

        <div class="fcard-body">
          <NuxtLink v-if="item.linkTo" :to="item.linkTo" class="fcard-title">{{ item.title }}</NuxtLink>
          <p v-else class="fcard-title">{{ item.title }}</p>
          <p class="fcard-sub">{{ item.artist || item.subtitle }}</p>
          <p v-if="item.note" class="fcard-note">{{ item.note }}</p>

          <p v-if="item.requireNewsletter && !subscribed" class="fcard-locked">
            🔒 Subscribers only
          </p>

          <div class="fcard-actions">
            <button
              class="btn-claim"
              :disabled="isBlocked(item)"
              @click="claim(item, 'mp3')"
            >
              {{ claimLabel(item, 'mp3') }}
            </button>
            <button
              v-if="item.allowWav"
              class="btn-claim btn-claim-alt"
              :disabled="isBlocked(item)"
              @click="claim(item, 'wav')"
            >
              {{ claimLabel(item, 'wav') }}
            </button>
          </div>

          <p v-if="item.claimCount > 0" class="fcard-count">
            {{ item.claimCount }} {{ item.claimCount === 1 ? 'person has' : 'people have' }} grabbed this
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
definePageMeta({ layout: 'shop' })

const router = useRouter()
const { $toast } = useNuxtApp()
const { loggedIn } = useUserSession()

const route = useRoute()

// Resolved during SSR, not in onMounted. The first version loaded here
// on mount, which meant the page shipped eight skeleton cards and only
// became real once the JavaScript ran — and stayed skeletons forever if
// anything went wrong on the way. `useRequestFetch` so the visitor's
// session cookie reaches the endpoint during SSR; a plain $fetch would
// drop it and report every signed-in customer as a guest.
const requestFetch = useRequestFetch()
const { data: pool, pending, refresh } = await useAsyncData(
  'shop-free-pool',
  () => requestFetch('/api/shop/free'),
  { default: () => ({ enabled: false, quota: null, items: [] }) }
)

const items = computed(() => pool.value?.items || [])
// Previewing runs through the shop's shared player, so a snippet
// started here keeps playing while the visitor browses on, and the
// queue/history behave exactly as they do everywhere else.
const player = useAudioPlayer()
const { currentTrack, isPlaying } = player

const isPreviewing = (item) =>
  !!(item.preview && currentTrack.value?.id === item.preview.id && isPlaying.value)

const togglePreview = (item) => {
  if (!item.preview) return
  player.toggle({
    id: item.preview.id,
    url: item.preview.url,
    title: item.preview.artist
      ? `${item.preview.title} — ${item.preview.artist}`
      : item.preview.title,
    imageUrl: item.preview.imageUrl,
    audio: { waveform: [] },
    previewStart: item.preview.previewStart,
    previewLength: item.preview.previewLength,
    previewOffset: item.preview.previewOffset || 0,
    // The FULL track length. The waveform is drawn from the whole song, so
    // without this the player scales it by the <audio> element's duration —
    // which holds only the preview slice. See MasterPlayer `fullDuration`.
    duration: item.preview.duration || 0,
  })
}


const enabled = computed(() => !!pool.value?.enabled)
const quota = computed(() => pool.value?.quota || null)
const busyId = ref(null)

// Still needed here (not just in the signup component): the claim
// buttons must know whether a newsletter-gated card is reachable.
const subscribed = ref(false)

const referral = ref(null)
const copied = ref(false)

const gatedCount = computed(() =>
  items.value.filter(i => i.requireNewsletter).length
)

/** A card's buttons are dead when the item is out of reach right now. */
function isBlocked(item) {
  if (busyId.value === item._id) return true
  if (item.owned) return false
  if (!loggedIn.value) return false          // click sends them to signup
  if (item.requireNewsletter && !subscribed.value) return true
  return quota.value?.totalAvailable === 0
}

const resetLabel = computed(() => {
  if (!quota.value?.resetsAt) return 'next week'
  const d = new Date(quota.value.resetsAt)
  return d.toLocaleDateString(undefined, { weekday: 'long' })
})

function claimLabel(item, format) {
  if (busyId.value === item._id) return 'Working…'
  const suffix = format === 'wav' ? 'WAV' : 'MP3'
  if (item.owned) return `Download ${suffix}`
  if (!loggedIn.value) return `Get ${suffix} free`
  if (item.requireNewsletter && !subscribed.value) return 'Subscribe first'
  if (quota.value?.totalAvailable === 0) return 'No claims left'
  return `Claim ${suffix}`
}

/**
 * Per-visitor extras only. The pool itself comes from useAsyncData above,
 * so a failure in either of these can no longer leave the page showing
 * skeletons — the listing is already rendered by the time they run.
 */
async function loadExtras() {
  try {
    const nl = await $fetch('/api/shop/newsletter/status')
    subscribed.value = !!nl.subscribed
  } catch { /* signed out, or lookup failed — treat as not subscribed */ }

  if (loggedIn.value) {
    try {
      referral.value = await $fetch('/api/shop/referral/stats')
    } catch { /* panel simply stays hidden */ }
  }
}

async function copyReferral() {
  try {
    await navigator.clipboard.writeText(referral.value.shareUrl)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch {
    $toast?.error('Could not copy — select the link manually')
  }
}

async function claim(item, format) {
  // Signed-out visitors get sent to signup with a return path, so the
  // claim they wanted is one click away after registering — this page is
  // the acquisition funnel, and bouncing them to a bare signup form
  // loses the intent that brought them.
  if (!loggedIn.value) {
    router.push(`/signup?next=${encodeURIComponent('/free')}`)
    return
  }

  busyId.value = item._id
  try {
    const res = await $fetch('/api/shop/free/claim', {
      method: 'POST',
      body: { itemId: item._id, format }
    })
    if (res.quota && pool.value) pool.value = { ...pool.value, quota: res.quota }
    // items is derived from `pool`, so update the source rather than
    // mutating the computed array.
    if (pool.value) {
      pool.value = {
        ...pool.value,
        items: (pool.value.items || []).map(i => i._id === item._id
          ? { ...i, owned: true, claimCount: (i.claimCount || 0) + (res.alreadyOwned ? 0 : 1) }
          : i)
      }
    }
    // Straight to the existing download page — same surface a paid order
    // lands on, including the asset tree for sample packs.
    await router.push(res.downloadUrl)
  } catch (e) {
    const denial = e?.data?.data?.denial
    if (denial === 'quota-exhausted' && e?.data?.data?.quota && pool.value) {
      pool.value = { ...pool.value, quota: e.data.data.quota }
    }
    // Server says the subscription isn't confirmed after all (e.g. the
    // visitor subscribed in another tab but never clicked the link).
    // Re-sync so the card stops offering a claim that cannot succeed.
    if (denial === 'newsletter-required') {
      subscribed.value = false
    }
    $toast?.error(e?.data?.statusMessage || 'Could not claim this download')
  } finally {
    busyId.value = null
  }
}

onMounted(async () => {
  // Stash a ?ref= code before anything else, so it is already on the
  // visitor's cookie if they head straight to signup from here.
  const ref = String(route.query.ref || '').trim()
  if (ref) {
    try {
      await $fetch('/api/shop/referral/track', { method: 'POST', body: { code: ref } })
    } catch { /* a bad code is simply ignored */ }
  }
  await loadExtras()
})
</script>

<style scoped>
.quota-strip {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 1rem;
  padding: 1rem 1.25rem; margin-bottom: 1.5rem;
  background: linear-gradient(135deg, rgb(var(--primary-channels) / 0.12), rgb(var(--primary-channels) / 0.02));
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
}

.panel {
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 1rem;
  padding: 0.9rem 1.15rem; margin-bottom: 1rem;
  background: var(--cy-module);
  border: 1px solid var(--cy-rim);
  border-radius: 3px;
}

.bonus-chip {
  display: inline-block; margin-left: 0.5rem;
  font-family: var(--cy-mono);
  font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em;
  padding: 2px 7px; border-radius: 2px;
  background: rgb(var(--success-channels) / 0.18); color: var(--success);
}

.nl-input {
  padding: 0.5rem 0.75rem; border-radius: 0.5rem;
  background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.12);
  color: #fff; font-size: 0.8rem; min-width: 200px;
}
.nl-input::placeholder { color: rgba(255,255,255,0.3); }

.ref-link {
  font-size: 0.7rem; color: rgba(255,255,255,0.6);
  background: rgba(0,0,0,0.35); padding: 0.45rem 0.65rem; border-radius: 0.4rem;
  /* Long share URLs must not stretch the panel on mobile. */
  max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.fcard-locked {
  font-family: var(--cy-mono);
  font-size: 0.62rem; color: var(--warning);
  background: rgb(var(--warning-channels) / 0.12); border-radius: 2px;
  padding: 2px 6px; align-self: flex-start;
  text-transform: uppercase; letter-spacing: 0.06em;
}

.grid-cards {
  display: grid; gap: 1rem;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
}

.fcard {
  background: var(--cy-module);
  border: 1px solid var(--cy-rim);
  border-radius: 3px; overflow: hidden;
  display: flex; flex-direction: column;
  transition: border-color 0.15s, transform 0.15s, box-shadow 0.15s;
}
.fcard:hover { border-color: var(--cy-rim-hot); transform: translateY(-2px); box-shadow: var(--cy-glow); }

.fcard-art-wrap { position: relative; aspect-ratio: 1; }
.fcard-art { width: 100%; height: 100%; object-fit: cover; display: block; }
.fcard-art-empty {
  display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.2); font-size: 2.5rem;
}

.badge-featured, .badge-owned {
  position: absolute; top: 0.5rem; left: 0.5rem;
  font-size: 9px; font-weight: 800; text-transform: uppercase;
  letter-spacing: 0.06em; padding: 3px 7px; border-radius: 4px;
}
.badge-featured { background: var(--primary); color: var(--cy-ground); font-family: var(--cy-mono); }
.badge-owned { top: auto; bottom: 0.5rem; background: color-mix(in srgb, var(--cy-ground) 75%, transparent); color: var(--primary); font-family: var(--cy-mono); }

.fcard-play {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: rgb(var(--primary-channels));
  color: var(--cy-ground, #0b0c10);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transform: scale(0.85);
  transition: opacity 0.18s var(--cy-ease, ease), transform 0.18s var(--cy-ease, ease);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}
.fcard:hover .fcard-play,
.fcard:focus-within .fcard-play,
.fcard-play.playing { opacity: 1; transform: scale(1); }
/* No hover on a touch screen — the control has to be there from the
   start or it cannot be reached at all. */
@media (pointer: coarse) {
  .fcard-play { opacity: 1; transform: scale(1); width: 44px; height: 44px; }
}
@media (prefers-reduced-motion: reduce) {
  .fcard-play { transition: none; transform: none; }
  .fcard:hover .fcard-play, .fcard-play.playing { transform: none; }
}

.fcard-body { padding: 0.75rem; display: flex; flex-direction: column; gap: 0.25rem; flex: 1; }
.fcard-title {
  color: var(--text-main); font-family: var(--cy-display); font-weight: 700; font-size: 0.875rem;
  line-height: 1.3; text-decoration: none;
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
a.fcard-title:hover { color: var(--primary); }
.fcard-sub { color: rgba(255, 255, 255, 0.45); font-size: 0.75rem; }
.fcard-note { color: rgba(255, 255, 255, 0.35); font-size: 0.7rem; font-style: italic; }
.fcard-count { color: rgba(255, 255, 255, 0.3); font-size: 0.65rem; margin-top: 0.25rem; }

.fcard-actions { display: flex; gap: 0.375rem; margin-top: auto; padding-top: 0.5rem; }

.btn-claim {
  flex: 1; padding: 0.55rem; border-radius: 3px;
  background: rgb(var(--primary-channels) / 0.2); border: 1px solid rgb(var(--primary-channels) / 0.4);
  color: var(--primary);
  font-family: var(--cy-mono);
  font-size: 0.66rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  min-height: 38px;
}
.btn-claim:hover:not(:disabled) {
  background: rgb(var(--primary-channels) / 0.32);
  border-color: var(--cy-rim-hot);
  box-shadow: var(--cy-glow);
}
.btn-claim:disabled { opacity: 0.4; cursor: not-allowed; }
/* WAV key — the quiet neutral frame (the blue literals ignored the theme). */
.btn-claim-alt {
  background: color-mix(in srgb, var(--text-main) 6%, transparent);
  border-color: var(--cy-rim);
  color: var(--text-muted);
}
.btn-claim-alt:hover:not(:disabled) {
  background: color-mix(in srgb, var(--text-main) 10%, transparent);
  color: var(--text-main);
}

.btn-primary {
  padding: 0.6rem 1rem; border-radius: 3px; background: var(--primary);
  color: var(--cy-ground);
  font-family: var(--cy-display);
  font-size: 0.72rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  text-decoration: none;
  filter: drop-shadow(0 0 12px var(--cy-glow-c));
}
.btn-ghost {
  padding: 0.6rem 1rem; border-radius: 3px;
  border: 1px solid var(--cy-rim);
  color: var(--text-muted);
  font-family: var(--cy-mono);
  font-size: 0.7rem; font-weight: 700;
  letter-spacing: 0.08em; text-transform: uppercase;
  text-decoration: none;
}
.btn-ghost:hover { border-color: var(--cy-rim-hot); color: var(--text-main); }

.empty {
  text-align: center; padding: 4rem 1rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px dashed rgba(255, 255, 255, 0.08); border-radius: 0.75rem;
}

.skeleton-card {
  aspect-ratio: 0.75; border-radius: 0.75rem;
  background: rgba(255, 255, 255, 0.04);
  animation: pulse 1.5s ease-in-out infinite;
}
@keyframes pulse { 0%, 100% { opacity: 1 } 50% { opacity: 0.5 } }
</style>
